import { OpenFeature, EvaluationContext } from '@openfeature/react-sdk';
import { FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider';

const instance = typeof window !== 'undefined' ? (window as any).instance || {} : {};

// Initialize the OpenFeature client with the Flagsmith provider.
// Both the API URL and the environment ID must switch per deployment
// (dev / staging / prod each has its own Flagsmith environment + key).
const flagsmithApiUrl = instance.flagsmithurl || process.env.FLAGSMITH_API_URL || '/flagsmith-api/api/v1/';
const flagsmithEnvironmentId =
  instance.flagsmithEnvironmentId || process.env.FLAGSMITH_ENVIRONMENT_ID || '';

if (!flagsmithEnvironmentId) {
  console.warn(
    '[openFeature] No Flagsmith environment ID configured. ' +
      'Set window.instance.flagsmithEnvironmentId (index.html) or FLAGSMITH_ENVIRONMENT_ID build env.'
  );
}

const flagsmithProvider = new FlagsmithClientProvider({
  environmentID: flagsmithEnvironmentId,
  api: flagsmithApiUrl,
  // Note: We removed `realtime: true` because your self-hosted backend does not 
  // currently support the default SSE stream at realtime.flagsmith.com.
  // We will fallback to a conservative polling interval.
});

/**
 * Builds an OpenFeature EvaluationContext from a JWT token.
 * Includes tenant, site_id, environment, user_id, email, and role.
 * 
 * Intentionally omits IP and Country to prevent sending frontend-guessed
 * traits. Null, undefined, or empty values are stripped.
 */
export const buildOpenFeatureContext = (
  token?: string | null,
  configEnvironment?: string
): EvaluationContext => {
  const environment = configEnvironment || instance.environment || process.env.APP_ENV || process.env.NODE_ENV || 'development';
  
  if (!token) {
    // If no token is provided, just set the environment
    return { targetingKey: 'anonymous', environment };
  }

  try {
    const { jwtDecode } = require('jwt-decode');
    const decoded = jwtDecode(token) as any;
    
    // Add console.log so you can verify exactly what is inside your token!
    console.log('[openFeature] Decoded JWT payload:', decoded);
    
    const context: EvaluationContext = {
      targetingKey: decoded.email || String(decoded.user_id ?? 'anonymous'),
      environment,
      traits: {}, // FlagsmithClientProvider v0.2.0 expects traits to be nested here!
    };

    // Explicit traits per requirement
    const extractedTraits: Record<string, any> = {
      user_id: decoded.user_id,
      site_id: decoded.site_id,
      email: decoded.email,
      tenant: decoded.tenant,
      role: decoded.role,
      environment: environment,
    };

    // Strip undefined, null, or empty string values and assign to both top-level (OpenFeature standard)
    // and context.traits (Flagsmith provider requirement)
    for (const [key, value] of Object.entries(extractedTraits)) {
      if (value !== undefined && value !== null && value !== '') {
        context[key] = value;
        (context.traits as Record<string, any>)[key] = value;
      }
    }

    console.log('[openFeature] Built EvaluationContext:', context);
    return context;
  } catch (error) {
    console.error('Failed to decode JWT for OpenFeature context', error);
    return { targetingKey: 'anonymous', environment };
  }
};

// Initialize the provider once on app startup.
// If the user is already logged in (token in sessionStorage from a previous session / page refresh),
// set the evaluation context immediately so the provider sends an identity request.
// If no token exists (login page), the provider starts anonymously — no identity spam.
export const openFeatureReady: Promise<void> = OpenFeature
  .setProviderAndWait(flagsmithProvider)
  .then(async () => {
    // Restore context from an existing session (handles page refresh)
    const existingToken = sessionStorage.getItem('token');
    if (existingToken) {
      await OpenFeature.setContext(buildOpenFeatureContext(existingToken));
    }
    // TODO: Since streaming (realtime: true) does not work with the current self-hosted 
    // setup, we fallback to a conservative 30-second polling interval.
    // When the self-hosted API supports SSE, replace this with realtime: true.
    flagsmithProvider.flagsmithClient.startListening(30000);
  })
  .catch((err) => {
    console.error('Failed to initialize OpenFeature provider', err);
  });

// Context updates run one at a time: logout doesn't await its clear, so without
// this a slow clear could land after the next login's sync and wipe its identity.
let contextQueue: Promise<void> = openFeatureReady;
const enqueueContextUpdate = (update: () => Promise<void>) => {
  contextQueue = contextQueue.then(update, update);
  return contextQueue;
};

export const syncOpenFeatureContext = () =>
  enqueueContextUpdate(async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (token) {
        await OpenFeature.setContext(buildOpenFeatureContext(token));
      }
    } catch (error) {
      console.error('Failed to sync OpenFeature context', error);
    }
  });

export const clearOpenFeatureContext = () =>
  enqueueContextUpdate(async () => {
    try {
      await OpenFeature.clearContext();
    } catch (error) {
      console.error('Failed to clear OpenFeature context', error);
    }
  });

export { OpenFeature };
